import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { Switch } from "antd";
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import ReactQuill from 'react-quill';
import { Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AnnouncementModal = ({ onSubmit, announcementData }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(false);
  const [publishedDate, setPublishedDate] = useState(new Date());
  const [unpublishedDate, setUnpublishedDate] = useState(null); // Optional unpublished date
  const [file, setFile] = useState(null); // State for the uploaded file
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const submitButtonRef = useRef(null);


  useEffect(() => {
    if (announcementData) {
      setTitle(announcementData.title);
      setDescription(announcementData.description);
      setStatus(announcementData.status);

      // Ensure the dates are converted to JavaScript Date objects
      setPublishedDate(announcementData.published_date ? new Date(announcementData.published_date) : new Date());
      setUnpublishedDate(announcementData.unpublished_date ? new Date(announcementData.unpublished_date) : null);
    } else {
      // Reset fields when no announcement is being edited
      setTitle("");
      setDescription("");
      setStatus(false);
      setPublishedDate(new Date());
      setUnpublishedDate(null);
    }
  }, [announcementData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("status", status);
    formData.append("publishedDate", publishedDate.toISOString());
    if (unpublishedDate) formData.append("unpublishedDate", unpublishedDate.toISOString());
    if (file) formData.append("file", file);

    try {
      let response;
      if (announcementData) {
        // Update existing announcement
        response = await axios.put(
          `${apiUrl}/announcements/update/${announcementData.id}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        toast.success("Announcement updated successfully!");
        if (submitButtonRef.current) {
          submitButtonRef.current.click(); // Trigger the click event
        }
      } else {
        // Add a new announcement
        response = await axios.post(`${apiUrl}/announcements/add`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        if (submitButtonRef.current) {
          submitButtonRef.current.click(); // Trigger the click event
        }
        toast.success("Announcement added successfully!");
      }

      onSubmit(response.data);
    } catch (error) {
      toast.error("Failed to save announcement. Please try again.");
    }
  };

  const handleFileChange = (info) => {
    const file = info.file.originFileObj;
    if (file.size / 1024 / 1024 > 2) {
      toast.error("File must be smaller than 2MB");
      return;
    }
    setFile(file);
  };

  return (
    <div id="add_announcement" className="modal custom-modal fade" role="dialog">
      <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "80%" }}>
        <div className="modal-content h-100">
          <div className="modal-header">
            <h5 className="modal-title">{announcementData ? "Edit Announcement" : "Add Announcement"}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Title <span className="text-danger">*</span></label>
              <div className="col-md-10">
                <input type="text" className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
            </div>

            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Description</label>
              <div className="col-md-10">
                <ReactQuill value={description} onChange={setDescription} theme="snow" style={{ height: "200px" }} />
              </div>
            </div>

            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Published Date</label>
              <div className="col-md-10">
                <DatePicker
                  selected={publishedDate}
                  onChange={(date) => setPublishedDate(date)}
                  className="form-control"
                  dateFormat="MMMM d, yyyy"
                />
              </div>
            </div>

            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Unpublished Date (Optional)</label>
              <div className="col-md-10">
                <DatePicker
                  selected={unpublishedDate}
                  onChange={(date) => setUnpublishedDate(date)}
                  className="form-control"
                  isClearable
                  placeholderText="Select a date (Optional)"
                  dateFormat="MMMM d, yyyy"
                />
              </div>
            </div>

            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Status</label>
              <div className="col-md-10">
                <Switch checked={status} onChange={(checked) => setStatus(checked)} /> Active
              </div>
            </div>

            <div className="form-group row mb-3">
              <label className="col-md-2 col-form-label">Upload Document</label>
              <div className="col-md-10">
                <Upload beforeUpload={() => false} maxCount={1} onChange={handleFileChange} accept=".pdf,.doc,.docx,.xls,.xlsx">
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
                <small className="text-muted">Max file size: 2MB</small>
              </div>
            </div>

            <hr style={{ border: "0.5px solid #ddd" }} />
          </div>
          <div className="modal-footer d-flex justify-content-center">
            <button type="submit" onClick={handleSubmit} className="btn btn-primary">
              {announcementData ? "Save Changes" : "Save"}
            </button>
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
              Cancel
            </button>
          </div>

          <div style={{ display: 'none' }}>
          <button ref={submitButtonRef} data-bs-dismiss="modal" aria-label="Close" type="reset">Submit2</button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
